'use client';
import { useEffect, useRef, useState } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import { activeLeadFormAtom } from '@core/Atoms/Leads/ActiveLeadForm.atom';
import { formStateAtom } from '@core/Atoms/Pdp/FormState.atom';
import { searchParamsAtom } from '@core/Atoms/SearchParams/SearchParams.atom';
import { userIdAtom } from '@core/Atoms/User/UserId.atom';
import Button from '@gds/Button/Button';
import useBreakpoint from '@gds/Pagination/Hooks/UseBreakpoint.hook';
import { generateLeadTrackingData } from '@lead-forms/Tracking/LeadForms.tracking';
import { trackCustomEvent } from '@tracking/Utils/TrackCustomEvent';

import { generateFloatingLeasingLinkTracking } from 'Components/LeasingPage/LeasingPage.tracking';

import type { LeasingLeadCTAsProps } from './LeasingLeadCTAs.entity';

import styles from '../LeadCTAs/LeadCTAs.module.css';

export const LeasingLeadCTAs = ({
  vehicle,
  strings,
  isSticky: isStickyCta,
}: LeasingLeadCTAsProps) => {
  const leadCTAsRef = useRef<HTMLDivElement>(null);
  const {
    breakpoints: { isMobile },
  } = useBreakpoint();
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const [isPhoneNumber, setShowPhoneNumber] = useState(false);

  const onLeadModal = () => {
    const {
      leadStart: { event, context },
    } = generateLeadTrackingData({
      searchParams,
      userId,
      formState,
      leadFormId: 'leasing',
      leadId: '',
      listingId: vehicle?.heycarId,
      vehicle,
      ctaLocation: 'leasing_pdp',
      leadAction: '',
    });
    trackCustomEvent({ event, context });
    toggleLeadForm('leasing-online');
  };

  useEffect(() => {
    const headerHeight = '-75'; // Fixed header height for mobile
    const options = {
      root: null,
      rootMargin: `${headerHeight}px 0px 0px 0px`,
      threshold: 0,
    };

    const observer = new IntersectionObserver(entries => {
      const query = window.matchMedia(`(max-width: 767px)`);

      if (!entries[0].isIntersecting && query.matches) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    }, options);

    if (leadCTAsRef.current) {
      observer.observe(leadCTAsRef.current);
    }

    return () => {
      if (leadCTAsRef.current) {
        observer.unobserve(leadCTAsRef.current);
      }
    };
  }, []);

  const userId = useAtomValue(userIdAtom);
  const searchParams = useAtomValue(searchParamsAtom);
  const formState = useAtomValue(formStateAtom);

  const handleLeasingCall = () => {
    const { callStart, callSubmit } = generateFloatingLeasingLinkTracking({
      searchParams,
      userId,
      formState,
      listingId: vehicle?.heycarId,
      leadType: 'leasing',
      ctaLocation: 'leasing_pdp',
      leadId: '',
      callNumber: strings.leasingCallUs,
    });

    if (isMobile) {
      window.location.href = `tel:${strings.leasingCallUs}`;
      trackCustomEvent({ event: callStart.event, context: callStart.context });
      trackCustomEvent({ event: callSubmit.event, context: callSubmit.context });
    } else {
      if (!isPhoneNumber) {
        setShowPhoneNumber(true);
        trackCustomEvent({ event: callStart.event, context: callStart.context });
      } else {
        window.location.href = `tel:${strings.leasingCallUs}`;
        trackCustomEvent({ event: callSubmit.event, context: callSubmit.context });
      }
    }
  };

  const shouldShowStickyCta = isSticky && isMobile;

  const toggleLeadForm = useSetAtom(activeLeadFormAtom);

  return (
    <div
      ref={leadCTAsRef}
      className={`${shouldShowStickyCta ? styles.minHeightZero : styles.leadButtonsOuter} `}
    >
      <div
        className={`${styles.leadButtonsWrapper} ${
          shouldShowStickyCta ? styles.stickyContent : ''
        } ${isStickyCta ? styles.stickyCta : ''}`}
      >
        <Button variant="contained" onClick={() => onLeadModal()}>
          {strings.messageUs}
        </Button>
        <Button variant="outlined" onClick={handleLeasingCall}>
          {isMobile
            ? strings.leasingCallText
            : isPhoneNumber
              ? strings.leasingCallUs
              : strings.leasingCallText}
        </Button>
      </div>
    </div>
  );
};
